import { useState } from 'react';
import {
  Badge,
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Textarea,
  Tooltip,
} from '@fluentui/react-components';
import {
  ArrowCounterclockwise16Regular,
  ArrowUndo16Regular,
  CheckmarkCircle16Filled,
  CheckmarkCircle16Regular,
  Info16Regular,
  ThumbDislike16Filled,
  ThumbDislike16Regular,
  ThumbLike16Filled,
  ThumbLike16Regular,
  Wrench16Regular,
} from '@fluentui/react-icons';
import type { FixAction, Issue, Severity, UnitLabel } from '@qcbot/shared';
import { navigateToTarget } from '../officeApi';
import { applyFix, undoFix, describePartialFix, FixDriftError, type FixSnapshot } from '../applyFix';
import { submitIssueFeedback, type ResolvedRuleSummary } from '../api';
import type { IssueCollab } from '../useRunCollab';
import { UserAvatar } from './UserAvatar';

const SEVERITY_COLOR: Record<Severity, 'danger' | 'important' | 'warning' | 'informative' | 'subtle'> = {
  Critical: 'danger',
  High: 'important',
  Medium: 'warning',
  Low: 'informative',
  Info: 'subtle',
};

/** An issue gets a Fix icon whenever it carries a mechanically-safe, exactly-verifiable fixAction.
 * Critical issues are fixable too, but only one at a time behind an explicit confirmation (see
 * `requestFix`) and never by "Fix all safe issues" — per product policy. */
function isFixEligible(issue: Issue): boolean {
  return Boolean(issue.fixAction);
}

/** Eligible for the one-click batch: everything fixable except Critical. */
function isBatchFixEligible(issue: Issue): boolean {
  return isFixEligible(issue) && issue.severity !== 'Critical';
}

/** Short human-readable preview of what a fix will change, for the tooltip and confirm dialog. */
function describeFix(action: FixAction | undefined, unit: UnitLabel = 'slide'): string {
  if (!action) return '';
  switch (action.type) {
    case 'replaceText':
      return `"${action.findText}" → "${action.replaceWith}"`;
    case 'setCellText':
      return (action.cells ?? []).map((c) => `"${c.findText}" → "${c.replaceWith}"`).join(', ');
    case 'setFontSize':
      return `Set "${action.findText?.slice(action.rangeOffset ?? 0, (action.rangeOffset ?? 0) + (action.rangeLength ?? 0))}" to ${action.fontSizePt}pt`;
    case 'setFontTypeface':
      return `Change font to "${action.fontTypeface}"`;
    case 'insertTextbox':
      return unit === 'slide' ? `Insert text box: "${action.insertText}"` : `Insert paragraph: "${action.insertText}"`;
    case 'composite': {
      const steps = action.steps ?? [];
      const units = new Set(steps.map((s) => s.slideNumber));
      const first = describeFix(steps[0]?.action, unit);
      return `${steps.length} edits on ${units.size} ${unit}${units.size === 1 ? '' : 's'}${first ? ` (e.g. ${first})` : ''}`;
    }
    default:
      return '';
  }
}

interface FixState {
  status: 'idle' | 'applying' | 'applied' | 'undoing' | 'error';
  snapshot?: FixSnapshot;
  error?: string;
}

const DEFAULT_FIX_STATE: FixState = { status: 'idle' };

interface FeedbackState {
  rating?: 'up' | 'down';
  showComment: boolean;
  comment: string;
  submitting: boolean;
  error?: string;
}

const DEFAULT_FEEDBACK: FeedbackState = { showComment: false, comment: '', submitting: false };

function withDefaults(state: FeedbackState | undefined): FeedbackState {
  return state ?? DEFAULT_FEEDBACK;
}

/** Tooltip content for the info icon next to each issue — the rule it's checking, or a note that
 * it's an AI-detected issue with no specific rule behind it (see agent/aiSuggestionPass.ts). */
function ruleTooltip(issue: Issue, rulesByControlId: Map<string, ResolvedRuleSummary> | undefined): string {
  if (!issue.controlId) return 'AI-detected issue — not tied to a specific template rule.';
  const rule = rulesByControlId?.get(issue.controlId);
  if (!rule) return 'Rule details unavailable.';
  return [rule.title, rule.severityGuidance ? `Default severity: ${rule.severityGuidance}` : undefined].filter(Boolean).join(' — ');
}

// NOTE: for very large decks (hundreds of issues) this should become a virtualized list
// (e.g. react-window) instead of rendering every row — acceptable for the initial scaffold.
export function IssueList({
  issues,
  runId,
  rulesByControlId,
  unitLabel = 'slide',
  unitAnchors,
  collab,
  emptyMessage = 'No issues found yet.',
}: {
  issues: Issue[];
  runId: string | null;
  rulesByControlId?: Map<string, ResolvedRuleSummary>;
  /** What this run's units are called — "slide" for a deck, "page"/"section" for a Word document. */
  unitLabel?: UnitLabel;
  /** Word documents: first paragraph of each page/section, so a page chip can jump to it. */
  unitAnchors?: Record<string, number>;
  /** Present once the run is finished: records who fixed what, and shows other people's activity. */
  collab?: IssueCollab;
  emptyMessage?: string;
}) {
  const unitNoun = unitLabel.charAt(0).toUpperCase() + unitLabel.slice(1);
  const [feedback, setFeedback] = useState<Record<string, FeedbackState>>({});
  const [fixState, setFixState] = useState<Record<string, FixState>>({});
  const [batchRunning, setBatchRunning] = useState(false);
  const [confirmIssue, setConfirmIssue] = useState<Issue | null>(null);
  const [claimConfirm, setClaimConfirm] = useState<{ issue: Issue; name: string } | null>(null);
  const [actionError, setActionError] = useState<Record<string, string>>({});

  if (issues.length === 0) {
    return <div style={{ color: '#666' }}>{emptyMessage}</div>;
  }

  function fixStateFor(issueId: string): FixState {
    return fixState[issueId] ?? DEFAULT_FIX_STATE;
  }

  /** Fixed on the server (by anyone, either way), or auto-fixed here and not left partial. */
  function isResolved(issue: Issue): boolean {
    if (issue.status === 'Fixed') return true;
    const fx = fixStateFor(issue.id);
    return Boolean(fx.snapshot) && describePartialFix(fx.snapshot!) === undefined;
  }

  function reportActionError(issueId: string, error: unknown) {
    setActionError((e) => ({ ...e, [issueId]: error instanceof Error ? error.message : 'Could not save' }));
  }

  function clearActionError(issueId: string) {
    setActionError((e) => {
      if (!(issueId in e)) return e;
      const { [issueId]: _removed, ...rest } = e;
      return rest;
    });
  }

  async function applyOne(issue: Issue) {
    collab?.claim(issue.id);
    setFixState((f) => ({ ...f, [issue.id]: { status: 'applying' } }));
    try {
      const snapshot = await applyFix(issue);
      setFixState((f) => ({ ...f, [issue.id]: { status: 'applied', snapshot } }));
      // A partially-applied multi-slide fix leaves real work on the skipped slides, so the issue
      // only counts as Fixed once every slide was fixed.
      const partial = describePartialFix(snapshot) !== undefined;
      if (collab && !partial) collab.resolve(issue.id, 'auto').catch((e) => reportActionError(issue.id, e));
    } catch (error) {
      const message = error instanceof FixDriftError ? error.message : error instanceof Error ? error.message : 'Could not apply this fix';
      setFixState((f) => ({ ...f, [issue.id]: { status: 'error', error: message } }));
    }
  }

  /** Someone else's claim and Critical severity each need an explicit confirm; otherwise apply straight away. */
  function requestFix(issue: Issue) {
    const others = collab?.claimedByOthers(issue.id) ?? [];
    if (others.length > 0) setClaimConfirm({ issue, name: others.map((o) => o.name).join(', ') });
    else if (issue.severity === 'Critical') setConfirmIssue(issue);
    else void applyOne(issue);
  }

  async function undoOne(issue: Issue) {
    const current = fixStateFor(issue.id);
    if (!current.snapshot) return;
    setFixState((f) => ({ ...f, [issue.id]: { status: 'undoing', snapshot: current.snapshot } }));
    try {
      await undoFix(current.snapshot);
      setFixState((f) => ({ ...f, [issue.id]: { status: 'idle' } }));
      if (collab) collab.reopen(issue.id).catch((e) => reportActionError(issue.id, e));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Could not undo this fix';
      // Keep the snapshot on a failed undo — the fix is still applied in the document, so the
      // user can retry Undo rather than losing the ability to revert.
      setFixState((f) => ({ ...f, [issue.id]: { status: 'error', snapshot: current.snapshot, error: message } }));
    }
  }

  async function markFixed(issue: Issue) {
    if (!collab) return;
    clearActionError(issue.id);
    try {
      await collab.resolve(issue.id, 'manual');
    } catch (error) {
      reportActionError(issue.id, error);
    }
  }

  async function reopen(issue: Issue) {
    if (!collab) return;
    clearActionError(issue.id);
    try {
      await collab.reopen(issue.id);
    } catch (error) {
      reportActionError(issue.id, error);
    }
  }

  /** A fixed issue collapses to one dim line saying who fixed it and how; only one way back is offered. */
  function renderResolved(issue: Issue, fx: FixState) {
    const resolution = issue.resolution;
    const mineOrLocal = !resolution || resolution.byUserId === collab?.me;
    const who = mineOrLocal || !resolution ? 'you' : resolution.byName;
    const label = !resolution && issue.status === 'Fixed' && !fx.snapshot ? 'Fixed' : (resolution?.method ?? 'auto') === 'auto' ? `Auto-fixed by ${who}` : `Marked fixed by ${who}`;
    // Only the pane that applied the edit holds the snapshot needed to revert the document.
    const canUndo = Boolean(fx.snapshot) && mineOrLocal;
    return (
      <div key={issue.id} style={{ border: '1px solid #e0e0e0', borderRadius: 6, padding: '6px 10px', background: '#fafafa' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <CheckmarkCircle16Filled style={{ color: '#2e7d32', flexShrink: 0 }} />
          <span style={{ fontSize: 12, flex: 1, minWidth: 0 }}>
            {issue.id} · {fx.status === 'undoing' ? 'Undoing…' : label}
          </span>
          {canUndo ? (
            <Button
              appearance="subtle"
              size="small"
              icon={<ArrowUndo16Regular />}
              disabled={fx.status === 'undoing'}
              title="Use this Undo button, not Ctrl+Z, to revert a QCBot fix"
              onClick={() => void undoOne(issue)}
            >
              Undo
            </Button>
          ) : (
            collab && (
              <Tooltip content="Marks it open again. The document is not changed." relationship="label">
                <Button appearance="subtle" size="small" icon={<ArrowCounterclockwise16Regular />} onClick={() => void reopen(issue)}>
                  Reopen
                </Button>
              </Tooltip>
            )
          )}
        </div>
        <button
          onClick={() => void navigateToTarget(issue.target, unitAnchors)}
          style={{ display: 'block', width: '100%', textAlign: 'left', border: 'none', background: 'none', padding: 0, cursor: 'pointer', fontSize: 11, color: '#666', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {issue.problem}
        </button>
        {fx.status === 'error' && <div style={{ fontSize: 11, color: '#b91c1c' }}>{fx.error}</div>}
        {actionError[issue.id] && <div style={{ fontSize: 11, color: '#b91c1c' }}>{actionError[issue.id]}</div>}
      </div>
    );
  }

  // "Auto-fix all" leaves out anything already done or that someone else is working on.
  const fixableIssues = issues.filter((i) => isBatchFixEligible(i) && !isResolved(i) && !collab?.claimedByOthers(i.id).length);
  // Undo all covers every fix applied from this pane, including Critical ones applied individually after confirm.
  const appliedIssues = issues.filter((i) => isFixEligible(i) && fixStateFor(i.id).status === 'applied');

  async function fixAll() {
    setBatchRunning(true);
    for (const issue of fixableIssues) {
      if (fixStateFor(issue.id).status === 'applied') continue;
      await applyOne(issue);
    }
    setBatchRunning(false);
  }

  async function undoAll() {
    setBatchRunning(true);
    for (const issue of appliedIssues) {
      await undoOne(issue);
    }
    setBatchRunning(false);
  }

  async function rate(issueId: string, rating: 'up' | 'down') {
    if (!runId) return;
    const base = withDefaults(feedback[issueId]);
    setFeedback((f) => ({ ...f, [issueId]: { ...base, rating, submitting: true, error: undefined } }));
    try {
      await submitIssueFeedback(runId, issueId, { rating, comment: base.comment || undefined });
      setFeedback((f) => ({ ...f, [issueId]: { ...f[issueId], submitting: false } }));
    } catch (error) {
      setFeedback((f) => ({ ...f, [issueId]: { ...f[issueId], submitting: false, error: error instanceof Error ? error.message : 'Failed to save' } }));
    }
  }

  async function submitComment(issueId: string) {
    if (!runId) return;
    const state = feedback[issueId];
    if (!state?.rating) return;
    setFeedback((f) => ({ ...f, [issueId]: { ...state, submitting: true, error: undefined } }));
    try {
      await submitIssueFeedback(runId, issueId, { rating: state.rating, comment: state.comment || undefined });
      setFeedback((f) => ({ ...f, [issueId]: { ...f[issueId], submitting: false, showComment: false } }));
    } catch (error) {
      setFeedback((f) => ({ ...f, [issueId]: { ...f[issueId], submitting: false, error: error instanceof Error ? error.message : 'Failed to save' } }));
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <Dialog open={confirmIssue !== null} onOpenChange={(_, data) => !data.open && setConfirmIssue(null)}>
        <DialogSurface style={{ margin: 16 }}>
          <DialogBody>
            <DialogTitle>Apply fix to a Critical issue?</DialogTitle>
            <DialogContent>
              <div style={{ fontSize: 13, marginBottom: 6 }}>{confirmIssue?.problem}</div>
              <div style={{ fontSize: 12, color: '#444' }}>This will change: {describeFix(confirmIssue?.fixAction, unitLabel)}</div>
              <div style={{ fontSize: 12, color: '#666', marginTop: 6 }}>Critical issues are usually fixed by hand. Check the result after applying — you can use Undo.</div>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setConfirmIssue(null)}>
                Cancel
              </Button>
              <Button
                appearance="primary"
                onClick={() => {
                  const issue = confirmIssue;
                  setConfirmIssue(null);
                  if (issue) void applyOne(issue);
                }}
              >
                Apply fix
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
      <Dialog open={claimConfirm !== null} onOpenChange={(_, data) => !data.open && setClaimConfirm(null)}>
        <DialogSurface style={{ margin: 16 }}>
          <DialogBody>
            <DialogTitle>{claimConfirm?.name} is working on this</DialogTitle>
            <DialogContent>
              <div style={{ fontSize: 13, marginBottom: 6 }}>{claimConfirm?.issue.problem}</div>
              <div style={{ fontSize: 12, color: '#666' }}>Auto-fixing now may clash with their edit.</div>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setClaimConfirm(null)}>
                Cancel
              </Button>
              <Button
                appearance="primary"
                onClick={() => {
                  const issue = claimConfirm?.issue;
                  setClaimConfirm(null);
                  if (!issue) return;
                  if (issue.severity === 'Critical') setConfirmIssue(issue);
                  else void applyOne(issue);
                }}
              >
                Apply anyway
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
      {(fixableIssues.length > 0 || appliedIssues.length > 0) && (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 2 }}>
          {fixableIssues.length > 0 && (
            <Button appearance="primary" size="small" disabled={batchRunning} onClick={() => void fixAll()}>
              Auto-fix all safe ({fixableIssues.length})
            </Button>
          )}
          {appliedIssues.length > 0 && (
            <Button appearance="secondary" size="small" disabled={batchRunning} onClick={() => void undoAll()}>
              Undo all ({appliedIssues.length})
            </Button>
          )}
        </div>
      )}
      {issues.map((issue) => {
        const otherSlides = (issue.affectedSlides ?? []).filter((s) => s !== issue.target.slideNumber);
        const fb = feedback[issue.id];
        const fx = fixStateFor(issue.id);
        const partialFix = fx.snapshot ? describePartialFix(fx.snapshot) : undefined;
        const fixEligible = isFixEligible(issue);
        if (isResolved(issue)) return renderResolved(issue, fx);
        const otherClaims = collab?.claimedByOthers(issue.id) ?? [];
        const mine = collab?.myClaim === issue.id;
        // Same 1px border as always; only its colour says who is working on the issue (mine wins if both).
        const claimColor = collab ? (mine && collab.me ? collab.colorOf(collab.me) : otherClaims.length > 0 ? collab.colorOf(otherClaims[0].userId) : undefined) : undefined;
        return (
          <div
            key={issue.id}
            style={{
              border: `1px solid ${claimColor ?? '#e0e0e0'}`,
              borderRadius: 6,
              padding: '8px 10px',
              background: 'white',
            }}
          >
            <button
              onClick={() => {
                collab?.claim(issue.id);
                void navigateToTarget(issue.target, unitAnchors);
              }}
              style={{
                textAlign: 'left',
                border: 'none',
                background: 'none',
                padding: 0,
                width: '100%',
                cursor: 'pointer',
              }}
            >
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 4, flexWrap: 'wrap' }}>
                <Badge color={SEVERITY_COLOR[issue.severity]}>{issue.id}</Badge>
                <Tooltip content={ruleTooltip(issue, rulesByControlId)} relationship="label">
                  <Info16Regular style={{ color: '#666', flexShrink: 0 }} />
                </Tooltip>
                {issue.origin === 'ai_suggested' && <span style={{ fontSize: 11, fontWeight: 600, color: '#8764b8' }}>AI</span>}
                <span style={{ fontSize: 11, color: '#666' }}>{unitNoun} {issue.target.slideNumber}</span>
                {otherClaims.length > 0 && (
                  <span style={{ marginLeft: 'auto', display: 'flex', gap: 2 }}>
                    {otherClaims.map((claim) => (
                      <Tooltip key={claim.userId} content={`${claim.name} is working on this`} relationship="label">
                        <UserAvatar size={16} name={claim.name} color={collab!.colorOf(claim.userId)} />
                      </Tooltip>
                    ))}
                  </span>
                )}
              </div>
              <div style={{ fontSize: 12 }}>{issue.problem}</div>
              <div style={{ fontSize: 11, color: '#444', paddingTop: '4px' }}>Recommendation: {issue.fix}</div>
            </button>
            {otherSlides.length > 0 && (
              <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
                <span style={{ fontSize: 11, color: '#666' }}>Also on:</span>
                {otherSlides.map((slideNumber) => (
                  <button
                    key={slideNumber}
                    onClick={() => navigateToTarget({ slideNumber, label: issue.target.label }, unitAnchors)}
                    title={`Go to ${unitLabel} ${slideNumber}`}
                    style={{
                      fontSize: 11,
                      border: '1px solid #d0d0d0',
                      borderRadius: 10,
                      padding: '1px 8px',
                      background: '#f5f5f5',
                      cursor: 'pointer',
                    }}
                  >
                    {slideNumber}
                  </button>
                ))}
              </div>
            )}
            {(runId || fixEligible || collab) && (
              <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  {runId && (
                    <>
                      <Button
                        appearance="subtle"
                        size="small"
                        icon={fb?.rating === 'up' ? <ThumbLike16Filled /> : <ThumbLike16Regular />}
                        disabled={fb?.submitting}
                        title="This issue is helpful"
                        onClick={() => void rate(issue.id, 'up')}
                      />
                      <Button
                        appearance="subtle"
                        size="small"
                        icon={fb?.rating === 'down' ? <ThumbDislike16Filled /> : <ThumbDislike16Regular />}
                        disabled={fb?.submitting}
                        title="This issue is not helpful"
                        onClick={() => void rate(issue.id, 'down')}
                      />
                      <Button
                        appearance="subtle"
                        size="small"
                        onClick={() =>
                          setFeedback((f) => {
                            const base = withDefaults(f[issue.id]);
                            return { ...f, [issue.id]: { ...base, showComment: !base.showComment } };
                          })
                        }
                      >
                        {fb?.showComment ? 'Hide feedback' : 'Add feedback'}
                      </Button>
                      {fb?.rating && !fb.submitting && !fb.error && <span style={{ fontSize: 11, color: '#2e7d32' }}>Thanks for the feedback</span>}
                      {fb?.error && <span style={{ fontSize: 11, color: '#b91c1c' }}>{fb.error}</span>}
                    </>
                  )}
                  {(fixEligible || collab) && (
                    <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
                      {fixEligible &&
                        (!fx.snapshot ? (
                          <Tooltip
                            content={describeFix(issue.fixAction, unitLabel) || 'QC Bot edits the document for you'}
                            relationship="label"
                          >
                            <Button
                              appearance="subtle"
                              size="small"
                              icon={<Wrench16Regular />}
                              disabled={fx.status === 'applying'}
                              onClick={() => requestFix(issue)}
                            >
                              {fx.status === 'applying' ? 'Applying…' : 'Auto-fix'}
                            </Button>
                          </Tooltip>
                        ) : (
                          <>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#9a6700' }}>
                              {fx.status === 'undoing' ? 'Undoing…' : 'Partly auto-fixed'}
                            </span>
                            <Button
                              appearance="subtle"
                              size="small"
                              icon={<ArrowUndo16Regular />}
                              disabled={fx.status === 'undoing'}
                              title="Use this Undo button, not Ctrl+Z, to revert a QCBot fix"
                              onClick={() => void undoOne(issue)}
                            >
                              Undo
                            </Button>
                          </>
                        ))}
                      {collab && (
                        <Tooltip content="I fixed this myself" relationship="label">
                          <Button appearance="subtle" size="small" icon={<CheckmarkCircle16Regular />} onClick={() => void markFixed(issue)}>
                            Mark fixed
                          </Button>
                        </Tooltip>
                      )}
                    </div>
                  )}
                </div>
                {fx.status === 'error' && <span style={{ fontSize: 11, color: '#b91c1c' }}>{fx.error}</span>}
                {actionError[issue.id] && <span style={{ fontSize: 11, color: '#b91c1c' }}>{actionError[issue.id]}</span>}
                {partialFix && (
                  <span style={{ fontSize: 11, color: '#9a6700' }}>
                    Fixed {partialFix.applied} of {partialFix.applied + partialFix.skipped.length} edits. Not applied — please fix by hand:{' '}
                    {partialFix.skipped.map((s) => `${unitLabel} ${s.slideNumber} (${s.reason})`).join('; ')}
                  </span>
                )}
                {fb?.showComment && (
                  <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                    <Textarea
                      size="small"
                      style={{ flex: 1 }}
                      placeholder="Optional details for the template owner…"
                      value={fb.comment}
                      onChange={(_, data) => setFeedback((f) => ({ ...f, [issue.id]: { ...f[issue.id], comment: data.value } }))}
                    />
                    <Button size="small" appearance="primary" disabled={!fb.rating || fb.submitting} onClick={() => void submitComment(issue.id)}>
                      Submit
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
